import { InputControl, TextInput } from "@growtherapy/sprout-ui";
import { ChangeEvent, FocusEvent, useCallback, useState } from "react";
import { validateVisitorName } from "./useVisitorName";
import { twMerge } from "tailwind-merge";
import {
  VISITOR_NAME_INPUT_LABEL,
  VISITOR_NAME_INPUT_OPTIONAL_SUFFIX,
  VISITOR_NAME_INPUT_PLACEHOLDER,
} from "../lang";

export type VisitorNameInputProps = {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  isRequired: boolean;
  label?: string;
  onEnter?: () => void;
  setVisitorName: (
    visitorName: string,
    validationResult: true | string,
  ) => void;
  visitorName: string;
};

export function VisitorNameInput({
  autoFocus,
  className,
  disabled,
  isRequired,
  label = VISITOR_NAME_INPUT_LABEL,
  onEnter,
  setVisitorName,
  visitorName,
}: VisitorNameInputProps) {
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string>();

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const validationResult = validateVisitorName(e.target.value, isRequired);
      // Only show validation error if the field has been touched, to prevent
      // over-aggressive error feedback while the user is filling in an initial
      // value.
      if (touched) {
        setError(validationResult !== true ? validationResult : undefined);
      }
      setVisitorName(e.target.value, validationResult);
    },
    [isRequired, touched, setVisitorName],
  );

  const handleOnBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      const validationResult = validateVisitorName(e.target.value, isRequired);
      setError(validationResult !== true ? validationResult : undefined);
    },
    [isRequired],
  );

  return (
    <InputControl
      className={twMerge("mb-0", className)}
      disabled={disabled}
      label={`${label}${isRequired ? "" : VISITOR_NAME_INPUT_OPTIONAL_SUFFIX}`}
      required={isRequired}
      error={error}
    >
      <TextInput
        // Generally this prop is only set when this field is the first or only
        // focusable field on the page and the form is the primary purpose of
        // that page. In this case, autofocusing the field is sensible because
        // it's where the user's attention is drawn anyway.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
        value={visitorName}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        onEnter={onEnter}
        mask="exclude"
        placeholder={VISITOR_NAME_INPUT_PLACEHOLDER}
        name="visitor-name"
        autoComplete="given-name"
      />
    </InputControl>
  );
}
